import { useAuth0 } from '@auth0/auth0-react';
import React, { useState, useEffect } from 'react';

import BetaWarningModal, {
  BETA_WARNING_MODAL_KEY,
} from './BetaWarningModal/BetaWarningModal';
import Sidebar from './Sidebar';

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { user } = useAuth0();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showBetaWarning, setShowBetaWarning] = useState(false);

  // Check if beta warning modal should be shown
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hasSeenValue = localStorage.getItem(BETA_WARNING_MODAL_KEY);
      const hasSeen = hasSeenValue ? JSON.parse(hasSeenValue) : false;
      if (!hasSeen) {
        setShowBetaWarning(true);
      }
    }
  }, []);

  // Handle responsive sidebar behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className='h-screen flex flex-col bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-50 overflow-hidden'>
      {/* Mobile hamburger menu button */}
      <button
        type='button'
        className='sm:hidden m-2 p-2 rounded-md bg-blue-600 text-white z-20 fixed top-0 left-0'
        onClick={() => setSidebarOpen(true)}
        aria-label='Open sidebar'
        style={{ display: sidebarOpen ? 'none' : 'block' }}
      >
        ☰
      </button>

      <div className='flex flex-1 overflow-hidden'>
        {/* Sidebar */}
        <div
          className={`${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } sm:translate-x-0 fixed sm:relative h-full z-30 transition-transform duration-300 ease-in-out`}
        >
          <Sidebar
            user={{
              picture: user?.picture,
              name: user?.name,
              email: user?.email,
            }}
            isOpen={sidebarOpen}
            onToggle={() => setSidebarOpen(!sidebarOpen)}
          />
        </div>

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className='sm:hidden fixed inset-0 bg-black bg-opacity-40 z-20'
            onClick={() => setSidebarOpen(false)}
            aria-hidden='true'
          />
        )}

        {/* Main content area */}
        <main className='flex-1 h-full overflow-y-auto bg-gray-50 dark:bg-gray-900'>
          {children}
        </main>
      </div>

      {/* Beta Warning Modal */}
      <BetaWarningModal
        isOpen={showBetaWarning}
        onClose={() => setShowBetaWarning(false)}
      />
    </div>
  );
};

export default AppLayout;
